import { useAuthStore } from '@/stores/authStore'
import { api } from './config'
import type { AxiosResponse } from 'axios'

/** Serialized subscription from GET /billing/shops/:shop_id/subscriptions (ids as strings). */
export type SubscriptionDTO = {
  _id: string
  organization_id: string
  ls_subscription_id: string
  ls_variant_id: string
  ls_customer_id: string
  ls_order_id?: string | null
  ls_order_item_id?: string | null
  entitlement: string
  plugin_id?: string | null
  monthly_price_cents: number
  currency: string
  status: string
  period_start?: string
  renews_at?: string | null
  cancelled_at?: string | null
  latest_event_id?: string | null
  created_at?: string
  updated_at?: string
}

export type CancelPreview = {
  endsAt: string | null
  monthlyPriceCents: number
  currency: string
}

export type CheckoutResponse = {
  checkoutUrl?: string
  variantChanged?: boolean
}

export type ShopQuota = {
  used: number
  max_shops: number | null
  can_create: boolean
}

export type ItemQuota = {
  used: number
  max_items: number | null
  remaining: number | null
  can_create: boolean
}

export type BillingState = {
  subscriptions: SubscriptionDTO[]
  entitlements: { plan: string; plugins: string[] }
  total_monthly_cents: number
  currency: string
  billing_email?: string | null
  lemonsqueezy_customer_id?: string | null
  shop_quota: ShopQuota
  item_quota: ItemQuota
}

type ApiResponse<T> = {
  data: T
}

const authHeaders = () => {
  const accessToken = useAuthStore.getState().auth.accessToken
  return {
    Authorization: `Bearer ${accessToken}`,
    'Content-Type': 'application/json',
  }
}

export async function getSubscriptions(shopId: string): Promise<BillingState> {
  return api
    .get(`billing/shops/${shopId}/subscriptions`, {
      headers: authHeaders(),
    })
    .then((response: AxiosResponse<ApiResponse<BillingState>>) => response.data.data)
}

export async function createPlanCheckout(
  shopId: string,
  planCode: 'PLUS' | 'PRO',
): Promise<CheckoutResponse> {
  return api
    .post(
      `billing/shops/${shopId}/checkout/plan`,
      { planCode },
      { headers: authHeaders() },
    )
    .then((response: AxiosResponse<ApiResponse<CheckoutResponse>>) => response.data.data)
}

export async function createPortalSession(
  shopId: string,
): Promise<{ portalUrl: string }> {
  return api
    .post(
      `billing/shops/${shopId}/portal`,
      {},
      { headers: authHeaders() },
    )
    .then((response: AxiosResponse<ApiResponse<{ portalUrl: string }>>) => response.data.data)
}

export async function previewCancelPlan(shopId: string): Promise<CancelPreview> {
  return api
    .post(
      `billing/shops/${shopId}/cancel/plan`,
      {},
      { params: { preview: true }, headers: authHeaders() },
    )
    .then((response: AxiosResponse<ApiResponse<CancelPreview>>) => response.data.data)
}

export async function cancelPlan(shopId: string): Promise<{ cancellationInitiated: true }> {
  const response = await api.post<{ data: { cancellationInitiated: true } }>(
    `billing/shops/${shopId}/cancel/plan`,
    {},
    {
      params: { preview: false },
      headers: authHeaders(),
      validateStatus: (status) => status === 202 || status === 200,
    },
  )
  return response.data.data
}

export async function previewCancelPlugin(
  shopId: string,
  pluginId: string,
): Promise<CancelPreview> {
  return api
    .post(
      `billing/shops/${shopId}/cancel/plugin/${pluginId}`,
      {},
      { params: { preview: true }, headers: authHeaders() },
    )
    .then((response: AxiosResponse<ApiResponse<CancelPreview>>) => response.data.data)
}

export async function cancelPlugin(
  shopId: string,
  pluginId: string,
): Promise<{ cancellationInitiated: true }> {
  const response = await api.post<{ data: { cancellationInitiated: true } }>(
    `billing/shops/${shopId}/cancel/plugin/${pluginId}`,
    {},
    {
      params: { preview: false },
      headers: authHeaders(),
      validateStatus: (status) => status === 202 || status === 200,
    },
  )
  return response.data.data
}
